import React from 'react';

import { RouteComponentProps } from 'react-router-dom';
import styled from 'styled-components';

import { useQuery } from '@apollo/react-hooks';
import _get from 'lodash/get';
import copy from '../copy';
import useDocumentTitle from '../hooks/use-document-title';
import useSegmentTracking from '../hooks/use-segment-tracking';
import useSift from '../hooks/use-sift';

import { createClient } from '../client';
import { CHURN_SURVEY_URL, REGISTER_ENDPOINT } from '../constants';
import {
  RegistrationFormGqlData,
  VariantBackgroundImage
} from '../interfaces/registration';
import { getQueryParam } from '../utils/query-utils';
import { getVariantFromQuery } from '../utils/string-utils';

import { GET_REGISTRATION_FORM_DATA } from '../queries/queries';
import {
  BannerType,
  ButtonImage,
  ErrorBanner,
  FormWrap,
  GithubButton,
  GoogleButton,
  HeaderBanner,
  Loading,
  PaddedContents,
  Wrap
} from './Common';
import Header from './Header';
import Metadata from './Metadata';
import RegistrationForm from './RegistrationForm';

import Button from '@do/walrus/Button';
import DOLink from '@do/walrus/DOLink';
import Link from '@do/walrus/Link';
import StepTransitionManager from '@do/walrus/StepTransitionManager';
import Text from '@do/walrus/Text';

import colors from '@do/walrus/scss/core/_colors.scss';
import vars from '@do/walrus/scss/core/_variables.scss';

import useOneTimeCookie from '@do/walrus/hooks/useOneTimeCookie';
import logoCreditSrc from '../assets/logo-credit.svg';
import logoEmailSrc from '../assets/logo-email.svg';
import { getSessionData } from '../utils/gql-utils';
import Background from './Background';

const CreditBanner = styled.div`
  background: ${colors.blue_lightest};
  padding: ${vars.whitespace_regular} ${vars.whitespace_medium};
  margin: -${vars.space_4} 0 ${vars.space_4};
  border-bottom: ${vars.border_width} solid ${colors.blue_darkest};
`;

const StepPaddedContents = styled(PaddedContents)`
  padding-bottom: ${vars.space_2};
`;

const CreditText = styled(Text)`
  margin: ${vars.whitespace_xsmall} 0 0;
  font-size: ${vars.font_size_small};
`;

const CreditImage = styled.img`
  margin: -${vars.whitespace_xsmall} ${vars.whitespace_small} 0;
  width: ${vars.space_4};
`;

const FooterWrap = styled(Text)`
  margin: ${vars.whitespace_regular} 0 0;
  font-size: ${vars.font_size_small};
`;
const client = createClient(REGISTER_ENDPOINT);
const userUUID = getSessionData('ajs_user_id').replace(/(^"|"$)/g, '');
const churnSurvey = userUUID
  ? `${CHURN_SURVEY_URL}?uuid=${userUUID}`
  : CHURN_SURVEY_URL;

function Registration({ location }: RouteComponentProps) {
  const [step, setStep] = React.useState(0);
  const isUserAccountDeactivated = useOneTimeCookie(
    '_digitalocean_flash_code',
    'user_account_deactivated'
  );

  const onboardingOrigin =
    getQueryParam('onboarding_origin', location.search) || '';

  const { loading, data } = useQuery<RegistrationFormGqlData>(
    GET_REGISTRATION_FORM_DATA,
    {
      client,
      variables: {
        onboarding_origin: onboardingOrigin,
        redirect_url: getQueryParam('redirect_url', location.search)
      }
    }
  );

  const variant = !loading && getVariantFromQuery(data);
  useDocumentTitle(_get(variant, 'title'));
  useSift(_get(data, 'registrationFormData.session_id'));
  useSegmentTracking(
    'Registration Variant',
    {
      variantType: _get(variant, 'type')
    },
    loading
  );

  if (!variant) {
    return <Loading />;
  }

  const oauthError = _get(data, 'registrationFormData.oauth_error');

  const googleOauthUrl = `/registrations/google${location.search}`;
  const githubOauthUrl = `/registrations/github${location.search}`;
  const signInUrl = _get(
    data,
    'registrationFormData.marketplace_app.create_app_url'
  );
  const registrationEmail = _get(data, 'registrationFormData.email');
  const OauthSection = () => (
    <>
      <Text>
        {copy.form.oauth.title + ' '}
        <Link
          forceExternal={true}
          includeContext={false}
          target="_self"
          to={googleOauthUrl}
        >
          {copy.form.oauth.google}
        </Link>
        {' ' + copy.form.or + ' '}
        <Link
          forceExternal={true}
          includeContext={false}
          target="_self"
          to={githubOauthUrl}
        >
          {copy.form.oauth.github}
        </Link>
        {' ' + copy.form.oauth.instead}
      </Text>
    </>
  );

  let form;
  if (step === 0) {
    form = (
      <>
        {oauthError && (
          <ErrorBanner error={copy.errors[oauthError] || copy.errors.unknown} />
        )}
        <h4>{variant.title}</h4>
        {variant.metadata ? (
          <Metadata variant={variant} data={data} />
        ) : (
          <Text>{variant.subtitle}</Text>
        )}

        {variant.type === 'team' && registrationEmail ? (
          <>
            <OauthSection />
            <RegistrationForm
              email={registrationEmail}
              onboardingOrigin={onboardingOrigin}
            />
          </>
        ) : (
          <>
            <Button fullWidth={true} onClick={() => setStep(1)}>
              <ButtonImage src={logoEmailSrc} />
              {copy.form.email_signup_title}
            </Button>

            <GoogleButton
              url={googleOauthUrl}
              text={copy.form.oauth.register.google_button}
            />

            <GithubButton
              url={githubOauthUrl}
              text={copy.form.oauth.register.github_button}
            />
          </>
        )}
      </>
    );
  } else {
    form = (
      <>
        <h4>{copy.form.email_signup_title}</h4>
        <OauthSection />
        <RegistrationForm
          email={registrationEmail}
          onboardingOrigin={onboardingOrigin}
        />
      </>
    );
  }

  let registrationBackground: React.ReactNode;
  if (variant && React.isValidElement(variant.background)) {
    registrationBackground = variant.background as React.ReactNode;
  } else if (variant) {
    const variantBackground = variant.background as VariantBackgroundImage;

    registrationBackground = (
      <Background
        horizontalPosition={variantBackground.horizontalPosition}
        verticalPosition={variantBackground.verticalPosition}
        url={variantBackground.image}
      />
    );
  }

  return (
    <>
      <Header
        forceExternal={!!signInUrl}
        url={signInUrl}
        queryString={location.search}
      />
      {isUserAccountDeactivated && (
        <HeaderBanner
          type={BannerType.Success}
          text={copy.deactivation_message}
          linkText={copy.survey_text}
          url={churnSurvey}
        />
      )}
      <Wrap>
        {registrationBackground}
        <FormWrap>
          {_get(data, 'registrationFormData.is_credit') && (
            <CreditBanner>
              <strong>
                {copy.credit.title}
                <CreditImage src={logoCreditSrc} />
              </strong>
              <CreditText>{copy.credit.details}</CreditText>
            </CreditBanner>
          )}
          <StepTransitionManager currentStepNum={step}>
            <StepPaddedContents>{form}</StepPaddedContents>
          </StepTransitionManager>

          <PaddedContents>
            <FooterWrap>
              {copy.footer.terms_before_link + ' '}
              <DOLink path="/legal/terms/">
                {copy.footer.terms_link_text}
              </DOLink>
              {' ' + copy.footer.privacy_before_link + ' '}
              <DOLink path="/legal/privacy/">
                {copy.footer.privacy_link_text}
              </DOLink>
            </FooterWrap>
          </PaddedContents>
        </FormWrap>
      </Wrap>
    </>
  );
}

export default Registration;
